import React, { useState } from "react";
import { Terminal, ScanLine, Check, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SubscribeForUpdates() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="9366" id="request-your-free-pool-deck-site-visit" ref={ref} className={`dark relative w-full py-24 overflow-hidden bg-background text-foreground transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 bg-background z-0" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-primary/5 blur-[120px] rounded-full pointer-events-none z-0" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] z-0 pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4 max-w-3xl">
        <Card className="bg-card/40 backdrop-blur-md border-primary/20 shadow-[0_0_50px_-12px_var(--color-primary)] rounded-sm overflow-hidden">
          <div className="h-1 w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-40" />
          
          <CardContent className="p-8 md:p-12 space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center gap-2 text-primary/80 font-mono text-xs tracking-widest uppercase mb-2">
                <Terminal className="w-4 h-4" />
                <span>Complimentary Consultation</span>
              </div>
              <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tight text-foreground uppercase drop-shadow-[0_0_10px_rgba(255,255,255,0.05)]">
                Start Your Pool Oasis
              </h2>
              <p className="font-default text-muted-foreground text-sm md:text-base max-w-lg mx-auto">
                Expert deck building since 1990. Request your free pool deck site visit and start your outdoor transformation.
              </p>
            </div>

            <div className="bg-black/10 border border-white/5 rounded-sm p-6 md:p-8">
              <FormProvider formKey="site_visit_form_pool_deck_contractors" formId="site_visit_form_pool_deck_contractors" sectionName="request-your-free-pool-deck-site-visit" defaultValues={{
              first_name: "",
              email: "",
              topics: []
            }} validationRules={{
              first_name: {
                required: false
              },
              email: {
                required: true,
                pattern: "email"
              },
              topics: {
                required: false
              }
            }} submitText="REQUEST SITE VISIT" successMessage="REQUEST RECEIVED. WE WILL CONTACT YOU SHORTLY." errorMessage="SUBMISSION ERROR. PLEASE TRY AGAIN.">
                <SubscribeForm />
              </FormProvider>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function SubscribeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center border border-primary/50 shadow-[0_0_30px_var(--color-primary)]">
          <Check className="w-8 h-8 text-primary" />
        </div>
        <h3 className="font-default text-xl text-primary tracking-widest uppercase font-bold">
          {successMessage}
        </h3>
        <p className="font-default text-sm text-muted-foreground">
          Our team is reviewing your project details now.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="font-default bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_10px_var(--color-primary)] transition-all duration-300 placeholder:text-muted-foreground/30 h-10" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required for consultation.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format."
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Email Address <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="font-default bg-transparent border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-[0_1px_10px_var(--color-primary)] transition-all duration-300 placeholder:text-muted-foreground/30 h-10" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="topics" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground block mb-2">
              Project Interests
            </FormLabel>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["Pool Decks", "Composite & Trex", "Structural Decks"].map(option => <label key={option} className="group flex items-center space-x-3 cursor-pointer">
                  <div className="relative flex items-center justify-center w-5 h-5 border border-muted-foreground/50 group-hover:border-primary transition-colors bg-transparent">
                    <input type="checkbox" className="peer appearance-none absolute inset-0 w-full h-full cursor-pointer" checked={field.value?.includes(option)} onChange={e => {
              const checked = e.target.checked;
              const current = field.value || [];
              field.onChange(checked ? [...current, option] : current.filter((val: string) => val !== option));
            }} />
                    {field.value?.includes(option) && <div className="w-3 h-3 bg-primary shadow-[0_0_8px_var(--color-primary)]" />}
                  </div>
                  <span className={`font-default text-sm transition-colors ${field.value?.includes(option) ? "text-primary" : "text-muted-foreground group-hover:text-foreground"}`}>
                    {option}
                  </span>
                </label>)}
            </div>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="p-3 border border-destructive/50 bg-destructive/10 text-destructive font-mono text-xs">
          ERROR: {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="group relative w-full md:w-auto min-w-[220px] h-12 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/50 rounded-[2px] overflow-hidden transition-all duration-300 hover:shadow-[0_0_20px_var(--color-primary)] hover:border-primary">
          <span className="relative z-10 flex items-center justify-center gap-2 font-default tracking-widest uppercase text-sm font-bold">
            {isSubmitting ? <>
                <Loader className="w-4 h-4 animate-spin" />
                Processing...
              </> : <>
                <ScanLine className="w-4 h-4" />
                Request Site Visit
              </>}
          </span>
          
          {}
          {!isSubmitting && <div className="absolute inset-0 w-[200%] h-full bg-gradient-to-r from-transparent via-primary/30 to-transparent -translate-x-[150%] group-hover:animate-[shimmer_1.5s_infinite] pointer-events-none" />}
        </Button>
      </div>
      
      <style jsx global>{`
        @keyframes shimmer {
          0% { transform: translateX(-150%) skewX(-20deg); }
          100% { transform: translateX(50%) skewX(-20deg); }
        }
      `}</style>
    </Form>;
}